import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as ExcelJS from "exceljs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Search,
  SortAsc,
  SortDesc,
  Edit,
  Trash2,
  Eye,
  Download,
  RefreshCw,
  MoreVertical,
  Calendar,
  Users,
  UserCheck,
  Building2,
  X,
  Activity,
} from "lucide-react";
import { baseURL } from "@/lib/axios";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AddEmployeeDialog,
  EmployeeFormValues,
} from "@/components/employees/AddEmployeeDialog";
import { format, isValid, parseISO } from "date-fns";
import { ViewEmployeeDialog } from "@/components/employees/ViewEmployeeDialog";
import {
  getEmployees,
  getEmployeeById,
  updateEmployee,
  deleteEmployee,
  type Employee,
  STATUS_OPTIONS,
} from "@/services/employee.service";
// import { PermissionGuard } from "@/components/PermissionGuard";
import { useAuthStore } from "@/stores/authStore";
import { PERMISSIONS } from "@/types/permissions";
import axiosInstance from "@/lib/axios";

// Add this helper function before the Employees component
const transformEmployeeToFormValues = (
  employee: Employee
): EmployeeFormValues => {
  const departmentName =
    typeof employee.department === "object"
      ? employee.department?.name || ""
      : employee.department || "";

  const departmentId =
    typeof employee.department === "object"
      ? employee.department?._id || employee.department?.id || ""
      : "";

  // Extract roles safely
  const roles = employee.user?.roles
    ? employee.user.roles
      .map((role: any) =>
        typeof role === "object" ? role.name || role : role
      )
      .filter(Boolean)
    : [];

  // Create a safe date object
  const joiningDate =
    employee.joinDate || employee.joiningDate
      ? new Date(employee.joinDate || employee.joiningDate || "")
      : new Date();

  // Get employeeId from either user object or employee object
  const employeeId = employee.employeeId || "";

  // IMPROVED: Extract phone number from multiple possible sources
  const phone = employee.phone || (employee.user as any)?.phone || "";
  const result = {
    firstName: employee.user?.firstName || "",
    lastName: employee.user?.lastName || "",
    email: employee.user?.email || "",
    phone: phone,
    department: departmentId || departmentName, // Use ID first, fallback to name
    roles: roles,
    employeeId: employeeId,
    joiningDate,
    salary: employee.salary || 0,
    departmentId: departmentId, // Add departmentId for proper matching
    // Add the missing nested objects
    jobInfo: {
      joiningDate: employee.jobInfo?.joiningDate
        ? new Date(employee.jobInfo.joiningDate)
        : new Date(),
      location: employee.jobInfo?.location || "",
      jobTitle: employee.jobInfo?.jobTitle || "",
      category: employee.jobInfo?.category || "",
      reportingManager: Array.isArray(employee.reportingManagers)
        ? employee.reportingManagers.map(emp => emp._id || emp.employeeId || emp.firstName + ' ' + emp.lastName)
        : Array.isArray(employee.jobInfo?.reportingManager)
          ? employee.jobInfo.reportingManager.map(emp => typeof emp === 'object' ? emp._id || emp.employeeId || emp.firstName + ' ' + emp.lastName : emp)
          : employee.jobInfo?.reportingManager
            ? [employee.jobInfo.reportingManager]
            : [],
      lineManager: Array.isArray(employee.lineManagers)
        ? employee.lineManagers.map(emp => emp._id || emp.employeeId || emp.firstName + ' ' + emp.lastName)
        : Array.isArray(employee.jobInfo?.lineManager)
          ? employee.jobInfo.lineManager.map(emp => typeof emp === 'object' ? emp._id || emp.employeeId || emp.firstName + ' ' + emp.lastName : emp)
          : employee.jobInfo?.lineManager
            ? [employee.jobInfo.lineManager]
            : [],
      lineManager2: Array.isArray(employee.lineManagers2)
        ? employee.lineManagers2.map(emp => emp._id || emp.employeeId || emp.firstName + ' ' + emp.lastName)
        : Array.isArray(employee.jobInfo?.lineManager2)
          ? employee.jobInfo.lineManager2.map(emp => typeof emp === 'object' ? emp._id || emp.employeeId || emp.firstName + ' ' + emp.lastName : emp)
          : employee.jobInfo?.lineManager2
            ? [employee.jobInfo.lineManager2]
            : [],
      employmentStatus: employee.jobInfo?.employmentStatus || "",
      probationPeriod: employee.jobInfo?.probationPeriod ? String(employee.jobInfo.probationPeriod) : "",
      probationEndDate: employee.jobInfo?.probationEndDate
        ? new Date(employee.jobInfo.probationEndDate)
        : undefined,
      feedbackRemainderDays: employee.jobInfo?.feedbackRemainderDays ? String(employee.jobInfo.feedbackRemainderDays) : "",
      notifyDay: employee.jobInfo?.notifyDay ? String(employee.jobInfo.notifyDay) : "",
      note: employee.jobInfo?.note || "",
    },
    agreementInfo: {
      startDate: employee.agreementInfo?.startDate
        ? new Date(employee.agreementInfo.startDate)
        : undefined,
      endDate: employee.agreementInfo?.endDate
        ? new Date(employee.agreementInfo.endDate)
        : undefined,
      periodInMonths: employee.agreementInfo?.periodInMonths || "",
      notifyPeriodInDays: employee.agreementInfo?.notifyPeriodInDays || "",
      note: employee.agreementInfo?.note || "",
    },
    compensationInfo: {
      startDate: employee.compensationInfo?.startDate
        ? new Date(employee.compensationInfo.startDate)
        : undefined,
      endDate: employee.compensationInfo?.endDate
        ? new Date(employee.compensationInfo.endDate)
        : undefined,
      newSalary: employee.compensationInfo?.newSalary || "",
      payFrequency: employee.compensationInfo?.payFrequency || "",
      noticePeriodInDays: employee.compensationInfo?.noticePeriodInDays || "",
      notifyPeriodInDays: employee.compensationInfo?.notifyPeriodInDays || "",
      salaryMode: employee.compensationInfo?.salaryMode || "",
      note: employee.compensationInfo?.note || "",
    },
    internshipInfo: {
      startDate: employee.internshipInfo?.startDate
        ? new Date(employee.internshipInfo.startDate)
        : undefined,
      endDate: employee.internshipInfo?.endDate
        ? new Date(employee.internshipInfo.endDate)
        : undefined,
      length: employee.internshipInfo?.length || "",
      reduceExtended: employee.internshipInfo?.reduceExtended || "",
      extendedDate: employee.internshipInfo?.extendedDate
        ? new Date(employee.internshipInfo.extendedDate)
        : undefined,

      note: employee.internshipInfo?.note || "",
    },
    address: {
      street: employee.address?.street || "",
      city: employee.address?.city || "",
      state: employee.address?.state || "",
      country: employee.address?.country || "",
      postalCode: employee.address?.postalCode || "",
    },
    emergencyContact: {
      name: employee.emergencyContact?.name || "",
      relationship: employee.emergencyContact?.relationship || "",
      phone: employee.emergencyContact?.phone || "",
    },
    bankDetails: {
      accountNumber: employee.bankDetails?.accountNumber || "",
      bankName: employee.bankDetails?.bankName || "",
      ifscCode: employee.bankDetails?.ifscCode || "",
    },
    isEdit: true,
  };

  return result;
};

export default function Employees() {
  const { hasPermission, hasSubModulePermission } = useAuthStore();
  const navigate = useNavigate();

  // State management
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [selectedRole, setSelectedRole] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({ key: "name", direction: "asc" });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [viewingEmployee, setViewingEmployee] = useState<Employee | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [totalEmployees, setTotalEmployees] = useState(0);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState<Employee | null>(
    null
  );

  // Departments and roles data for edit mode
  const [_departments, setDepartments] = useState<
    Array<{ _id?: string; id?: string; name: string }>
  >([]);
  const [roles, setRoles] = useState<
    Array<{ _id?: string; id?: string; name: string }>
  >([]);

  // Helper function to map backend data to frontend format
  const mapBackendDataToFrontend = (employeesData: any[]): Employee[] => {
    return employeesData.map((emp: any) => {
      const mapped = {
        ...emp,
        name:
          emp.name && emp.name.trim() !== ""
            ? emp.name
            : emp.firstName && emp.lastName
              ? `${emp.firstName} ${emp.lastName}`
              : emp.firstName || emp.lastName || "N/A",
        department:
          emp.department && typeof emp.department === "object"
            ? (emp.department as any).name || "N/A"
            : emp.department || "N/A",
        role:
          emp.role && typeof emp.role === "object"
            ? (emp.role as any).name || "N/A"
            : emp.role || "N/A",
        joinDate: emp.jobInfo?.joiningDate || emp.joiningDate || emp.joinDate || "",
        status: emp.jobInfo?.employmentStatus || emp.status || "Active",
        email: emp.email || "N/A",
        employeeId: emp.employeeId || emp.user?.employeeId,
      };
      return mapped;
    });
  };

  // Fetch initial data only once
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch employees and process them first
        const response = await getEmployees();

        // Normalize response - backend may return array or { data: [] } object
        const employeesData: any[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [];

        const mappedEmployees: Employee[] =
          mapBackendDataToFrontend(employeesData);
        setAllEmployees(mappedEmployees);

        // Conditionally fetch departments only if the user has permission
        let deptList: any[] = [];
        if (
          hasSubModulePermission("employee_management", "department", "view")
        ) {
          try {
            const deptResponse = await axiosInstance.get("/departments");
            deptList =
              deptResponse.data.data ||
              (Array.isArray(deptResponse.data) ? deptResponse.data : []);
          } catch (deptError) { }
        }
        // Fallback: derive unique departments from employee data
        if (deptList.length === 0) {
          const derived = Array.from(
            new Set(
              mappedEmployees.map((emp) => {
                if (emp.department && typeof emp.department === "object") {
                  return (emp.department as any).name;
                }
                return emp.department;
              })
            )
          )
            .filter(Boolean)
            .map((name: any, idx) => ({ id: idx, name }));
          deptList = derived;
        }
        setDepartments(deptList);

        // Conditionally fetch roles only if the user has permission
        let roleList: any[] = [];
        if (
          hasSubModulePermission("settings", "role", "manage") ||
          hasSubModulePermission("settings", "role", "view")
        ) {
          try {
            const roleResponse = await axiosInstance.get("/roles");
            roleList =
              roleResponse.data.data ||
              (Array.isArray(roleResponse.data) ? roleResponse.data : []);
          } catch (roleError) { }
        }
        if (roleList.length === 0) {
          const derivedRoles = Array.from(
            new Set(
              mappedEmployees.flatMap((emp) => {
                if (emp.user?.roles && Array.isArray(emp.user.roles)) {
                  return emp.user.roles.map((r: any) => {
                    if (r && typeof r === "object") {
                      return (r as any).name;
                    }
                    return r;
                  });
                }
                return [emp.role];
              })
            )
          )
            .filter(Boolean)
            .map((name: any, idx) => ({ id: idx, name }));
          roleList = derivedRoles;
        }
        setRoles(roleList);

        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    fetchData();
  }, [hasPermission, hasSubModulePermission]);

  // Filter and sort data in memory
  useEffect(() => {
    let filteredEmployees = allEmployees.filter((emp: Employee) => {
      const matchesSearch =
        !searchTerm ||
        (emp.user?.firstName &&
          typeof emp.user.firstName === "string" &&
          emp.user.firstName
            .toLowerCase()
            .includes(searchTerm.toLowerCase())) ||
        (emp.user?.email &&
          typeof emp.user.email === "string" &&
          emp.user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.user?.roles &&
          Array.isArray(emp.user.roles) &&
          emp.user.roles
            .join(", ")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()));

      // Department filter: improved logic to handle different data structures
      let employeeDepartmentId = "";
      let employeeDepartmentName = "";

      // Check if department is an object with name property
      if (emp.department && typeof emp.department === "object") {
        employeeDepartmentId =
          (emp.department as any)._id || (emp.department as any).id || "";
        employeeDepartmentName = (emp.department as any).name || "";
      }
      // Check if department is a string
      else if (typeof emp.department === "string") {
        employeeDepartmentName = emp.department;
        // Try to find the department ID by name
        const foundDept = _departments.find(
          (dept) => dept.name === emp.department
        );
        employeeDepartmentId = foundDept?._id || foundDept?.id || "";
      }
      // Check if department exists in the original data structure
      else if (emp.department) {
        employeeDepartmentName = String(emp.department);
      }

      // Department matching logic - check both ID and name
      let matchesDepartment = selectedDepartment === "All";
      if (!matchesDepartment) {
        // Check if selectedDepartment is a department ID
        if (
          employeeDepartmentId &&
          employeeDepartmentId === selectedDepartment
        ) {
          matchesDepartment = true;
        }
        // Check if selectedDepartment is a department name
        else if (
          employeeDepartmentName &&
          employeeDepartmentName === selectedDepartment
        ) {
          matchesDepartment = true;
        }
        // Check if selectedDepartment matches any department by name in the departments list
        else if (employeeDepartmentName) {
          const foundDept = _departments.find(
            (dept) =>
              dept._id === selectedDepartment ||
              dept.id === selectedDepartment ||
              dept.name === selectedDepartment
          );
          if (foundDept && foundDept.name === employeeDepartmentName) {
            matchesDepartment = true;
          }
        }
      }

      const matchesRole =
        selectedRole === "All" ||
        (() => {
          // First check if user.roles exists and is an array
          if (emp.user?.roles && Array.isArray(emp.user.roles)) {
            if (emp.user.roles.length > 0) {
              return emp.user.roles.some((role: any) => {
                const roleName =
                  typeof role === "object" ? role.name || role : role;
                return roleName === selectedRole;
              });
            }
          }
          // Then check the mapped role field
          if (emp.role) {
            const roleName =
              emp.role && typeof emp.role === "object"
                ? (emp.role as any).name || "Not provided"
                : emp.role;
            return roleName === selectedRole;
          }
          return false;
        })();

      const matchesStatus =
        selectedStatus === "All" ||
        emp.jobInfo?.employmentStatus === selectedStatus ||
        emp.status === selectedStatus;

      return matchesSearch && matchesDepartment && matchesRole && matchesStatus;
    });

    // Sort data
    filteredEmployees.sort((a: Employee, b: Employee) => {
      const aValue = a[sortConfig.key as keyof Employee] || "";
      const bValue = b[sortConfig.key as keyof Employee] || "";
      if (sortConfig.direction === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    // --- Glitch Fix: Recalculate pagination after data change ---
    setTotalEmployees(filteredEmployees.length);
    const totalPages = Math.ceil(filteredEmployees.length / pageSize);
    const newCurrentPage = Math.max(1, Math.min(currentPage, totalPages));

    // Pagination
    const startIndex = (newCurrentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedEmployees = filteredEmployees.slice(startIndex, endIndex);

    setEmployees(paginatedEmployees);

    // If the page was out of bounds, update it.
    if (currentPage !== newCurrentPage) {
      setCurrentPage(newCurrentPage);
    }
    // --- End of Fix ---
  }, [
    allEmployees,
    searchTerm,
    selectedDepartment,
    selectedRole,
    selectedStatus,
    sortConfig,
    currentPage,
    pageSize,
    _departments,
  ]);

  // Handle sort
  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Actions handlers
  const handleView = async (employee: Employee) => {
    try {
      setLoading(true);
      const responseData = await getEmployeeById(employee._id!);
      const employeeData = responseData.data || responseData;
      setViewingEmployee(employeeData);
      setIsViewDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (employee: Employee) => {
    try {
      setLoading(true);
      const responseData = await getEmployeeById(employee._id!);
      const employeeData = responseData.data || responseData;

      // Use type assertion since we know this is an Employee type
      const updatedEmployeeData = {
        ...employeeData,
        employeeId: employee.employeeId, // Keep the original employeeId from the list view
      } as Employee;

      setEditingEmployee(updatedEmployeeData);
      setIsEditDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!employeeToDelete) return;

    try {
      setLoading(true);
      // Use the correct ID field - try _id first, then id
      const employeeId = employeeToDelete._id || employeeToDelete.id;

      if (!employeeId) {
        throw new Error("Employee ID not found");
      }

      await deleteEmployee(employeeId);

      // Close dialog
      setDeleteDialogOpen(false);
      setEmployeeToDelete(null);

      // Refresh the employee list
      const deleteRefreshResponse = await getEmployees();
      const deleteEmployeesData: any[] = Array.isArray(deleteRefreshResponse)
        ? deleteRefreshResponse
        : Array.isArray(deleteRefreshResponse?.data)
          ? deleteRefreshResponse.data
          : [];
      const mappedEmployees: Employee[] =
        mapBackendDataToFrontend(deleteEmployeesData);
      setAllEmployees(mappedEmployees);

      toast.success("Employee deleted successfully");
    } catch (error: any) {
      // Handled globally
    } finally {
      setLoading(false);
    }
  };

  // Refresh data handler (manual refresh)
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await getEmployees();
      // Normalize response - backend may return array or { data: [] } object
      const employeesData: any[] = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];
      const mappedEmployees: Employee[] =
        mapBackendDataToFrontend(employeesData);
      setAllEmployees(mappedEmployees);
    } catch (error) {
      // Handled globally
    } finally {
      setIsRefreshing(false);
    }
  };

  // Export to CSV
  const handleExport = async () => {
    try {
      const data = await getEmployees();
      const mappedEmployees: Employee[] = mapBackendDataToFrontend(data);

      // Filter data for export based on current view
      const filteredData = mappedEmployees.filter((emp: Employee) => {
        const matchesSearch =
          !searchTerm ||
          emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (typeof emp.role === "string"
            ? emp.role.toLowerCase().includes(searchTerm.toLowerCase())
            : "");

        const employeeDepartmentName =
          typeof emp.department === "object"
            ? emp.department?.name
            : emp.department;

        const matchesDepartment =
          selectedDepartment === "All" ||
          (employeeDepartmentName &&
            employeeDepartmentName === selectedDepartment);
        const matchesRole =
          selectedRole === "All" ||
          (() => {
            if (
              emp.user?.roles &&
              Array.isArray(emp.user.roles) &&
              emp.user.roles.length > 0
            ) {
              return emp.user.roles.some((role: any) => {
                const roleName =
                  typeof role === "object" ? role.name || role : role;
                return roleName === selectedRole;
              });
            }
            if (emp.role) {
              const roleName =
                typeof emp.role === "object"
                  ? emp.role.name || "N/A"
                  : emp.role;
              return roleName === selectedRole;
            }
            return false;
          })();
        const matchesStatus =
          selectedStatus === "All" || emp.status === selectedStatus;

        return (
          matchesSearch && matchesDepartment && matchesRole && matchesStatus
        );
      });

      // Sort by Employee ID in ascending order
      filteredData.sort((a, b) => {
        const idA = a.employeeId || 0;
        const idB = b.employeeId || 0;

        // Alphanumeric sort for robust comparison
        return String(idA).localeCompare(String(idB), undefined, {
          numeric: true,
        });
      });

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Employees");

      // Define columns and headers with styling
      worksheet.columns = [
        { header: "Employee ID", key: "employeeId", width: 15 },
        { header: "Full Name", key: "fullName", width: 30 },
        { header: "Email", key: "email", width: 30 },
        { header: "Phone", key: "phone", width: 20 },
        { header: "Department", key: "department", width: 25 },
        { header: "Role(s)", key: "roles", width: 25 },
        { header: "Status", key: "status", width: 15 },
        { header: "Joining Date", key: "joiningDate", width: 20 },
        { header: "Salary", key: "salary", width: 20 },
      ];

      // Style Header
      const headerRow = worksheet.getRow(1);
      headerRow.height = 30;
      headerRow.eachCell((cell: ExcelJS.Cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF4F46E5" }, // Professional Blue
        };
        cell.font = {
          bold: true,
          color: { argb: "FFFFFFFF" },
          size: 14,
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Freeze Header Row
      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      // Prepare and add data rows
      filteredData.forEach((emp) => {
        const firstName = emp.user?.firstName || emp.firstName || "";
        const lastName = emp.user?.lastName || emp.lastName || "";
        const fullName = `${firstName} ${lastName}`.trim() || emp.name;

        const email = emp.user?.email || emp.email || "N/A";
        const phone = emp.phone || (emp.user as any)?.phone || "N/A";

        const department =
          typeof emp.department === "object"
            ? emp.department?.name || "N/A"
            : emp.department || "N/A";

        let roles = "N/A";
        if (
          emp.user?.roles &&
          Array.isArray(emp.user.roles) &&
          emp.user.roles.length > 0
        ) {
          roles = emp.user.roles
            .map((r: any) => (typeof r === "object" ? r.name || r : r))
            .join(", ");
        } else if (emp.role) {
          roles =
            typeof emp.role === "object" ? emp.role.name || "N/A" : emp.role;
        }

        const status = emp.jobInfo?.employmentStatus || emp.status || "Active";
        const joiningDate = emp.joinDate
          ? format(parseISO(String(emp.joinDate)), "PPP")
          : "N/A";
        const salary = emp.salary || undefined;

        const row = worksheet.addRow({
          employeeId: emp.employeeId || "N/A",
          fullName,
          email,
          phone,
          department,
          roles,
          status,
          joiningDate,
          salary,
        });

        // Style data rows
        row.height = 25;
        row.eachCell(
          { includeEmpty: true },
          (cell: ExcelJS.Cell, colNumber: number) => {
            cell.font = { size: 12 };
            cell.alignment = {
              vertical: "middle",
              horizontal: "left",
              wrapText: true,
            };
            cell.border = {
              top: { style: "thin", color: { argb: "FFD4D4D8" } },
              left: { style: "thin", color: { argb: "FFD4D4D8" } },
              bottom: { style: "thin", color: { argb: "FFD4D4D8" } },
              right: { style: "thin", color: { argb: "FFD4D4D8" } },
            };

            // Apply colorful zebra-striping (alternating row colors)
            if (row.number % 2 === 1) {
              // For rows 3, 5, 7, etc.
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFEBF8FF" }, // A light, pleasant blue
              };
            } else {
              // For rows 2, 4, 6, etc.
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFF7FAFC" }, // A very light, cool grey
              };
            }

            // Salary formatting
            if (
              worksheet.columns[colNumber - 1].key === "salary" &&
              typeof cell.value === "number"
            ) {
              cell.numFmt = '"₹"#,##0.00';
              cell.alignment = { ...cell.alignment, horizontal: "right" };
            }
          }
        );

        // Conditional formatting for Status
        const statusCell = row.getCell("status");
        const statusValue = statusCell.value;
        if (statusValue === "Active" || statusValue === "Full-time") {
          statusCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "D1FAE5" },
          }; // Green-100
          statusCell.font = { ...statusCell.font, color: { argb: "047857" } }; // Green-700
        } else if (statusValue === "Inactive" || statusValue === "Terminated") {
          statusCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FEE2E2" },
          }; // Red-100
          statusCell.font = { ...statusCell.font, color: { argb: "B91C1C" } }; // Red-700
        } else if (statusValue === "On Leave" || statusValue === "Probation") {
          statusCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FEF3C7" },
          }; // Amber-200
          statusCell.font = { ...statusCell.font, color: { argb: "92400E" } }; // Amber-800
        } else if (statusValue === "Part-time" || statusValue === "Contract") {
          statusCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "E0E7FF" },
          }; // Blue-100
          statusCell.font = { ...statusCell.font, color: { argb: "1D4ED8" } }; // Blue-700
        } else if (statusValue === "Intern") {
          statusCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "F3E8FF" },
          }; // Purple-100
          statusCell.font = { ...statusCell.font, color: { argb: "7C3AED" } }; // Purple-700
        }
      });

      // Generate and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `employees_${format(new Date(), "yyyy-MM-dd")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("Employee data has been exported");
    } catch (error) {
      // Handled globally
    }
  };

  // Simple refresh for add employee dialog
  const handleAddEmployeeRefresh = async () => {
    try {
      const response = await getEmployees();
      // Normalize response - backend may return array or { data: [] } object
      const employeesData: any[] = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];
      const mappedEmployees: Employee[] = mapBackendDataToFrontend(employeesData);
      setAllEmployees(mappedEmployees);
    } catch (error) {
      console.error('Failed to refresh employee list after create:', error);
      toast.error("Refresh Failed", {
        description: "Employee was created but list could not refresh. Please refresh manually.",
      });
    }
  };

  // Handle edit submit
  const handleEditSubmit = async (data: EmployeeFormValues) => {
    if (!editingEmployee) return;

    try {
      // Use the correct ID field - try _id first, then id
      const employeeId = editingEmployee._id || editingEmployee.id;
      if (!employeeId) {
        throw new Error("Employee ID not found");
      }

      await updateEmployee(employeeId, data);

      // Close dialog first
      setIsEditDialogOpen(false);

      // Refresh data
      const editRefreshResponse = await getEmployees();
      const editEmployeesData: any[] = Array.isArray(editRefreshResponse)
        ? editRefreshResponse
        : Array.isArray(editRefreshResponse?.data)
          ? editRefreshResponse.data
          : [];

      // Map backend data to frontend display format
      const mappedEmployees: Employee[] =
        mapBackendDataToFrontend(editEmployeesData);

      setAllEmployees(mappedEmployees);

      toast.success("Employee updated successfully");
    } catch (error: any) {
      // Handled globally
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalEmployees / pageSize);

  return (
    <div className="space-y-4 p-4 pt-1 pb-10">
      <div className="flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Employee Management
            </h1>
          </div>
          <div className="flex items-center space-x-4">
            {hasPermission(PERMISSIONS.EMPLOYEES_CREATE) && (
              <AddEmployeeDialog
                mode="add"
                onRefresh={handleAddEmployeeRefresh}
                departments={_departments}
                roles={roles}
              />
            )}
          </div>
        </div>

        {/* Stats Cards */}
        {allEmployees.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
                    <Users className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-medium text-muted-foreground">
                      Total Employees
                    </div>
                    <div className="text-xl sm:text-2xl font-bold">
                      {allEmployees.length}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-green-100 rounded-lg">
                    <UserCheck className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-medium text-muted-foreground">
                      Active Employees
                    </div>
                    <div className="text-xl sm:text-2xl font-bold">
                      {allEmployees.filter((e) => e.status === "Active" || e.status === "Full-time").length}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow sm:col-span-2 lg:col-span-1">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="p-2 sm:p-3 bg-purple-100 rounded-lg">
                    <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-medium text-muted-foreground">
                      Departments
                    </div>
                    <div className="text-xl sm:text-2xl font-bold">
                      {
                        allEmployees
                          .map((e) => e.department)
                          .filter((v, i, a) => a.indexOf(v) === i).length
                      }
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters Card */}
        <Card className="border border-gray-200 shadow-none rounded-xl mb-6 bg-white">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or role..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-10 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1 h-6 w-6"
                    onClick={() => setSearchTerm("")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <Select
                value={selectedDepartment}
                onValueChange={setSelectedDepartment}
              >
                <SelectTrigger className="h-10 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white">
                  <Building2 className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Departments</SelectItem>
                  {_departments.map((department) => (
                    <SelectItem
                      key={department.id || department._id}
                      value={department.id || department._id || department.name}
                    >
                      {department.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="h-10 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white">
                  <Users className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Roles</SelectItem>
                  {roles.map((role) => (
                    <SelectItem key={role.id || role._id} value={role.name}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex gap-2">
                <Select
                  value={selectedStatus}
                  onValueChange={setSelectedStatus}
                >
                  <SelectTrigger className="h-10 rounded-lg border-gray-200 focus:border-blue-500 focus:ring-blue-500 text-sm bg-white">
                    <Activity className="mr-2 h-4 w-4" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Status</SelectItem>
                    {STATUS_OPTIONS.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedDepartment("All");
                    setSelectedRole("All");
                    setSelectedStatus("All");
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>

                <Button
                  onClick={handleExport}
                  size="icon"
                  className="h-10 w-10 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
                >
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Employee List */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Employee List</CardTitle>
              </div>
              <div className="flex items-center space-x-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(v) => setPageSize(Number(v))}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Rows per page" />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 10, 20, 50].map((size) => (
                      <SelectItem key={size} value={size.toString()}>
                        {size} rows
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                      >
                        <RefreshCw
                          className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""
                            }`}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Refresh Data</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-x-auto">
              <Table className="min-w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs sm:text-sm w-16">Photo</TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center space-x-1">
                        <span className="text-xs sm:text-sm">Name</span>
                        {sortConfig.key === "name" &&
                          (sortConfig.direction === "asc" ? (
                            <SortAsc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ) : (
                            <SortDesc className="h-3 w-3 sm:h-4 sm:w-4" />
                          ))}
                      </div>
                    </TableHead>
                    <TableHead className="text-xs sm:text-sm">Email</TableHead>
                    <TableHead className="text-xs sm:text-sm">
                      Department
                    </TableHead>
                    <TableHead className="text-xs sm:text-sm">Role</TableHead>
                    <TableHead className="text-xs sm:text-sm">Status</TableHead>
                    <TableHead>
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="text-xs sm:text-sm">Join Date</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-right text-xs sm:text-sm">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Skeleton className="h-8 w-8 rounded-full" />
                        </TableCell>
                        <TableCell className="font-medium">
                          <Skeleton className="h-5 w-32" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-48" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-6 w-24 rounded-md" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-32" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-6 w-20 rounded-md" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-24" />
                        </TableCell>
                        <TableCell className="text-right">
                          <Skeleton className="h-8 w-8 ml-auto" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : isRefreshing ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <div className="flex flex-col items-center space-y-2">
                          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
                          <span className="text-sm text-muted-foreground">
                            Loading employees...
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : employees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8">
                        <div className="flex flex-col items-center space-y-2">
                          <span className="text-sm text-muted-foreground">
                            No employees found
                          </span>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setSearchTerm("");
                              setSelectedDepartment("All");
                              setSelectedRole("All");
                              setSelectedStatus("All");
                            }}
                          >
                            Clear Filters
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    employees.map((employee) => (
                      <TableRow key={employee.id}>
                        <TableCell>
                          <div className="flex items-center justify-center">
                            {employee.profilePicture ? (
                              <img
                                src={employee.profilePicture.startsWith('http')
                                  ? employee.profilePicture
                                  : `${baseURL.replace('/api/v1', '')}${employee.profilePicture}`}
                                alt={`${employee.user?.firstName || ''} ${employee.user?.lastName || ''}`}
                                className="h-8 w-8 rounded-full object-cover border border-gray-200"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                }}
                              />
                            ) : null}
                            <div className={`h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center ${employee.profilePicture ? 'hidden' : ''}`}>
                              <Users className="h-4 w-4 text-white" />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-xs sm:text-sm">
                          {hasPermission(PERMISSIONS.EMPLOYEES_EDIT) || hasPermission(PERMISSIONS.EMPLOYEES_CREATE) ? (
                            <button
                              onClick={() => navigate(`/employee/${employee._id || employee.id}`)}
                              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                            >
                              {employee.user?.firstName && employee.user?.lastName
                                ? `${employee.user.firstName} ${employee.user.lastName}`
                                : employee.user?.firstName ||
                                employee.user?.lastName ||
                                "Not provided"}
                            </button>
                          ) : (
                            <span>
                              {employee.user?.firstName && employee.user?.lastName
                                ? `${employee.user.firstName} ${employee.user.lastName}`
                                : employee.user?.firstName ||
                                employee.user?.lastName ||
                                "Not provided"}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          {employee.user?.email || "Not provided"}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="bg-blue-50 text-blue-700 text-xs"
                          >
                            {typeof employee.department === "object"
                              ? employee.department?.name
                              : employee.department || ""}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          {/* Fix role display - check multiple possible sources */}
                          {(() => {
                            // First check if user.roles exists and is an array
                            if (
                              employee.user?.roles &&
                              Array.isArray(employee.user.roles)
                            ) {
                              if (employee.user.roles.length > 0) {
                                return employee.user.roles
                                  .map((role: any) =>
                                    typeof role === "object"
                                      ? role.name || role
                                      : role
                                  )
                                  .join(", ");
                              }
                            }
                            // Then check the mapped role field
                            if (employee.role) {
                              return typeof employee.role === "object"
                                ? employee.role.name || "Not provided"
                                : employee.role;
                            }
                            // Default fallback
                            return "Not provided";
                          })()}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`text-xs ${employee.status === "Active" || employee.status === "Full-time"
                              ? "bg-green-50 text-green-700"
                              : employee.status === "Inactive" || employee.status === "Terminated"
                                ? "bg-red-50 text-red-700"
                                : employee.status === "On Leave" || employee.status === "Probation"
                                  ? "bg-yellow-50 text-yellow-700"
                                  : employee.status === "Part-time" || employee.status === "Contract"
                                    ? "bg-blue-50 text-blue-700"
                                    : employee.status === "Intern"
                                      ? "bg-purple-50 text-purple-700"
                                      : "bg-gray-50 text-gray-700"
                              }`}
                          >
                            {employee.status || "Active"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          {employee.joinDate &&
                            isValid(parseISO(String(employee.joinDate)))
                            ? format(parseISO(String(employee.joinDate)), "PPP")
                            : ""}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 sm:h-9 sm:w-9"
                                    >
                                      <MoreVertical className="h-3 w-3 sm:h-4 sm:w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                </TooltipTrigger>
                                <TooltipContent>Actions</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <DropdownMenuContent
                              align="end"
                              className="w-[160px]"
                            >
                              {hasPermission(PERMISSIONS.EMPLOYEES_VIEW) && (
                                <DropdownMenuItem
                                  onClick={() => handleView(employee)}
                                >
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Details
                                </DropdownMenuItem>
                              )}
                              {hasPermission(PERMISSIONS.EMPLOYEES_EDIT) && (
                                <DropdownMenuItem
                                  onClick={() => handleEdit(employee)}
                                >
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit Employee
                                </DropdownMenuItem>
                              )}
                              {hasPermission(PERMISSIONS.EMPLOYEES_DELETE) &&
                                (hasPermission(PERMISSIONS.EMPLOYEES_EDIT) ||
                                  hasPermission(
                                    PERMISSIONS.EMPLOYEES_VIEW
                                  )) && <DropdownMenuSeparator />}
                              {hasPermission(PERMISSIONS.EMPLOYEES_DELETE) && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setEmployeeToDelete(employee);
                                    setDeleteDialogOpen(true);
                                  }}
                                  className="text-red-600"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete Employee
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {!loading && !isRefreshing && employees.length > 0 && (
              <div className="flex items-center justify-between mt-4 px-4">
                <div className="text-sm text-gray-500">
                  Showing {(currentPage - 1) * pageSize + 1} to{" "}
                  {Math.min(currentPage * pageSize, totalEmployees)} of{" "}
                  {totalEmployees} entries
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="h-8 px-3 text-sm"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="default"
                    className="h-8 w-8 p-0 text-sm bg-[#000000] hover:bg-[#000000] text-white"
                    disabled
                  >
                    {currentPage}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="h-8 px-3 text-sm"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View Employee Dialog */}
      <ViewEmployeeDialog
        employee={viewingEmployee}
        open={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
      />

      {/* Edit Employee Dialog */}
      {hasSubModulePermission("employee_management", "employee", "edit") &&
        editingEmployee && (
          <AddEmployeeDialog
            mode="edit"
            employee={transformEmployeeToFormValues(editingEmployee)}
            open={isEditDialogOpen}
            onOpenChange={(open) => {
              setIsEditDialogOpen(open);
              if (!open) {
                setEditingEmployee(null);
              }
            }}
            onEmployeeEdit={handleEditSubmit}
            onRefresh={handleAddEmployeeRefresh}
            departments={_departments}
            roles={roles}
          />
        )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this employee? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
